import { useState } from 'react';
import { FontIcon } from '@fluentui/react';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  MessageBar,
  MessageBarBody,
  OverlayDrawer,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  PagedDataGrid,
  useActiveTheme,
  useFetch,
  useIsMobile,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import { profileDataAtom } from '../../atoms/authAtoms';
import { appRoles } from '../../authConfig';
import {
  getOrderStatuses,
  type IOrderStatus,
} from '../../services/workService';
import OrderStatusForm from './OrderStatusForm';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '100%',
  },
  colorBar: {
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    display: 'block',
  },
  statusIcon: {
    fontSize: tokens.fontSizeBase600,
  },
});

export default function OrderStatusSettings() {
  const styles = useStyles();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const [selected, setSelected] = useState<IOrderStatus>();
  const [editing, setEditing] = useState<IOrderStatus>();
  const statusFetch = useFetch<IOrderStatus[]>({
    queryKey: ['orderStatuses'],
    queryFn: getOrderStatuses,
  });

  const columns: DataGridColumn<IOrderStatus>[] = [
    { key: 'id', name: 'Id', width: 60, value: (status) => String(status.id) },
    { key: 'name', name: 'Name', minWidth: 160, width: 240 },
    { key: 'code', name: 'Code', minWidth: 120, width: 160 },
    {
      key: 'icon',
      name: 'Icon',
      width: 80,
      renderCell: (status) =>
        status.icon ? (
          <FontIcon iconName={status.icon} className={styles.statusIcon} />
        ) : null,
    },
    {
      key: 'color',
      name: 'Color',
      width: 120,
      renderCell: (status) =>
        status.color ? (
          <span
            title={status.color}
            className={styles.colorBar}
            style={{ backgroundColor: status.color }}
          />
        ) : null,
    },
  ];

  const commands: ICommandBarItem[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      icon: <FontIcon iconName="Refresh" />,
      onClick: () => statusFetch.refetch(),
    },
    {
      key: 'editStatus',
      text: 'Edit',
      icon: <FontIcon iconName="Edit" />,
      disabled: !isAdmin || !selected,
      onClick: () => setEditing(selected),
    },
  ];

  const closeDrawer = () => setEditing(undefined);

  return (
    <div className={styles.page}>
      <Text>
        All tenants use the same order lifecycle and labels. Invoiced and Paid
        are managed through billing. Only the icon and colour can be changed.
      </Text>
      {statusFetch.error && (
        <MessageBar intent="error">
          <MessageBarBody>
            {getApiErrorMessage(
              statusFetch.error,
              'Failed to load order statuses.'
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      <PagedDataGrid<IOrderStatus>
        columns={columns}
        items={statusFetch.data ?? []}
        loading={statusFetch.loading}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        onSelectionChange={setSelected}
        onRowDoubleClicked={(status) => {
          if (isAdmin) setEditing(status);
        }}
        fillContainer
        empty={{
          title: 'No order statuses',
          subtitle: 'The order lifecycle has not been configured yet.',
        }}
      />
      <FluentProvider theme={theme}>
        <OverlayDrawer
          open={!!editing}
          onOpenChange={(_, data) => {
            if (!data.open) closeDrawer();
          }}
          position="end"
          size={isMobile ? 'full' : 'medium'}
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={closeDrawer}
                />
              }
            >
              {editing ? `${editing.name} (${editing.code})` : ''}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            {editing && (
              <OrderStatusForm
                status={editing}
                onClose={closeDrawer}
                onSaved={(message) => {
                  closeDrawer();
                  setSuccessMessage(message);
                  statusFetch.refetch();
                }}
              />
            )}
          </DrawerBody>
        </OverlayDrawer>
      </FluentProvider>
    </div>
  );
}
